import { localeRowDirection } from '@/lib/rtl-layout';
import { useRef, useState } from "react";
import { View } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import type { SmartCVPublicProject } from "@tabbio/types";
import { Text, TextInput } from "../../common/AppText";
import { NativeButton } from "../../common/NativeButton";
import { useTheme } from "../../../hooks/useTheme";
import { useFont } from "../../../providers/FontProvider";
import { useLocale } from "../../../hooks/useLocale";
import { useToast } from "../../../hooks/useToast";
import { useDirtyGuard } from "../../../hooks/useDirtyGuard";
import {
  useUpsertProject,
  useDeleteProject,
} from "../../../hooks/useProfileApi";
import { useCVData } from "../../../lib/cv-mock-data";
import { confirmDeleteCvItem } from "./delete-confirmation";
import { cvChangeNotApplied } from "../../../lib/cv-write-coordinator";

const blankProject = (): SmartCVPublicProject => ({
  id: "",
  title: "",
  description: "",
  link: "",
  technologies: [],
});

/** One acknowledged project write at a time; no optimistic multi-row batch. */
export function EditProjectsContent() {
  const { t } = useTranslation("seeker");
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const toast = useToast();
  const { id } = useLocalSearchParams<{ id?: string }>();
  const { profile, profileRef, updateProfile, target } = useCVData();
  const upsert = useUpsertProject(target ?? id);
  const remove = useDeleteProject(target ?? id);
  const [draft, setDraft] = useState<SmartCVPublicProject | null>(null);
  const [technologies, setTechnologies] = useState("");
  const original = useRef("");
  const busy = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const serialize = (item: SmartCVPublicProject, tech: string) =>
    JSON.stringify([item, tech]);
  const open = (item: SmartCVPublicProject) => {
    const copy = { ...item, technologies: [...item.technologies] };
    const tech = copy.technologies.join("\n");
    original.current = serialize(copy, tech);
    setDraft(copy);
    setTechnologies(tech);
    setError("");
  };
  const save = async (): Promise<boolean> => {
    if (!draft || busy.current) return false;
    if (!draft.title.trim()) {
      setError(t("cv.edit.projectTitleRequired"));
      return false;
    }
    const link = draft.link?.trim() ?? "";
    if (link) {
      try {
        if (!["http:", "https:"].includes(new URL(link).protocol))
          throw new Error();
      } catch {
        setError(t("cv.edit.projectLinkInvalid"));
        return false;
      }
    }
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      const existing = profileRef.current.projects ?? [];
      const index = existing.findIndex((item) => item.id === draft.id);
      const saved = await upsert.mutateAsync({
        ...(draft.id ? { id: draft.id } : {}),
        title: draft.title.trim(),
        description: draft.description ?? "",
        link,
        // Images are not edited here, so omission preserves stored URLs.
        technologies: technologies
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean),
        order: index < 0 ? existing.length : index,
      });
      if (!saved?.id) throw new Error("Missing saved project");
      const latest = [...(profileRef.current.projects ?? [])];
      const position = latest.findIndex((item) => item.id === draft.id);
      const next = { ...draft, ...saved };
      if (position < 0) latest.push(next);
      else latest[position] = next;
      updateProfile({ projects: latest });
      setDraft(null);
      toast.success(t("cv.edit.saved"));
      return true;
    } catch (error) {
      // B02: a CV conflict the user answered is not a failure. "Discard"
      // closes this project's editor (the list keeps the saved version);
      // "keep editing" keeps the draft open.
      const outcome = cvChangeNotApplied(error);
      if (outcome === "discarded") {
        setDraft(null);
        return true;
      }
      if (outcome === "kept") return false;
      toast.error(t("cv.edit.saveFailed"));
      return false;
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  useDirtyGuard(
    saving ||
      Boolean(draft && serialize(draft, technologies) !== original.current),
    save,
  );
  const deleteItem = (item: SmartCVPublicProject) => {
    void confirmDeleteCvItem(t, "project", async () => {
      if (busy.current) return;
      busy.current = true;
      setSaving(true);
      try {
        await remove.mutateAsync(item.id);
        updateProfile({
          projects: (profileRef.current.projects ?? []).filter(
            (entry) => entry.id !== item.id,
          ),
        });
        toast.success(t("cv.edit.saved"));
      } catch (error) {
        // A conflict the user answered (B02) already said what happened.
        if (!cvChangeNotApplied(error)) toast.error(t("cv.edit.saveFailed"));
      } finally {
        busy.current = false;
        setSaving(false);
      }
    });
  };
  const inputStyle = {
    color: theme.text,
    backgroundColor: theme.surface,
    borderColor: theme.border,
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    fontFamily: fonts.regular,
    fontSize: 16,
    textAlign: isRTL ? ("right" as const) : ("left" as const),
  };
  const field = (
    key: "title" | "description" | "link",
    label: string,
    multiline = false,
  ) => (
    <View style={{ gap: 6 }}>
      <Text style={{ color: theme.textSecondary }}>{label}</Text>
      <TextInput
        accessibilityLabel={label}
        value={draft?.[key] ?? ""}
        editable={!saving}
        onChangeText={(value) =>
          setDraft((current) => current && { ...current, [key]: value })
        }
        multiline={multiline}
        textAlignVertical="top"
        autoCapitalize={key === "link" ? "none" : "sentences"}
        style={[
          inputStyle,
          multiline && { minHeight: 130 },
          key === "link" && { writingDirection: "ltr", textAlign: "left" },
        ]}
      />
    </View>
  );
  return (
    <View style={{ gap: 16 }}>
      {draft ? (
        <>
          {field("title", t("cv.edit.projectTitle"))}
          {field("description", t("cv.edit.projectDescription"), true)}
          <Text style={{ color: theme.textSecondary }}>
            {t("cv.edit.projectTechnologies")}
          </Text>
          <TextInput
            accessibilityLabel={t("cv.edit.projectTechnologies")}
            value={technologies}
            onChangeText={setTechnologies}
            editable={!saving}
            multiline
            textAlignVertical="top"
            style={[inputStyle, { minHeight: 90 }]}
          />
          {field("link", t("cv.edit.projectLink"))}
          {error ? (
            <Text accessibilityRole="alert" style={{ color: theme.text }}>
              {error}
            </Text>
          ) : null}
          <NativeButton
            title={t("cv.edit.save")}
            onPress={() => {
              void save();
            }}
            loading={saving}
          />
          <NativeButton
            title={t("cv.edit.cancel")}
            onPress={() => setDraft(null)}
            disabled={saving}
            variant="ghost"
          />
        </>
      ) : (
        <>
          <NativeButton
            title={t("cv.edit.addProject")}
            onPress={() => open(blankProject())}
            disabled={saving}
          />
          {!profile.projects?.length ? (
            <Text style={{ color: theme.textSecondary }}>
              {t("cv.edit.noProjects")}
            </Text>
          ) : null}
          {(profile.projects ?? []).map((item) => (
            <View
              key={item.id}
              style={{
                padding: 16,
                gap: 10,
                backgroundColor: theme.surface,
                borderRadius: 16,
              }}
            >
              <Text style={{ color: theme.text, fontFamily: fonts.semibold }}>
                {item.title}
              </Text>
              {item.description ? (
                <Text style={{ color: theme.textSecondary }}>
                  {item.description}
                </Text>
              ) : null}
              <View
                style={{ flexDirection: localeRowDirection(isRTL), gap: 8 }}
              >
                <NativeButton
                  title={t("common:actions.edit")}
                  onPress={() => open(item)}
                  disabled={saving}
                  variant="soft"
                  size="sm"
                />
                <NativeButton
                  title={t("cv.edit.delete")}
                  onPress={() => deleteItem(item)}
                  disabled={saving}
                  color="danger"
                  variant="ghost"
                  size="sm"
                />
              </View>
            </View>
          ))}
        </>
      )}
    </View>
  );
}
